import { EmptyReportOverlay } from '@report/components';
import { Box, Tab, TabContext, TabList, TabPanel } from '@shared/components';

import {
  type Template,
  useIsQuestionnaireReportFilterApplied,
  useQuestionnaireReportSearchParams,
} from '../../hooks';
import { QuestionnaireReportLongFormTemplate } from '../questionnaire-report-long-form-template';
import { QuestionnaireReportShortFormTemplate } from '../questionnaire-report-short-form-template';
import { QuestionnaireReportStandardTemplate } from '../questionnaire-report-standard-template';

const TEMPLATE_CONTENT: Record<Template, () => React.ReactElement> = {
  standard: QuestionnaireReportStandardTemplate,
  longForm: QuestionnaireReportLongFormTemplate,
  shortForm: QuestionnaireReportShortFormTemplate,
};

interface TabItem {
  value: Template;
  label: string;
}

const TAB_LIST: TabItem[] = [
  {
    value: 'standard',
    label: 'Template 1 - Standard',
  },
  {
    value: 'longForm',
    label: 'Template 2 - Long Form',
  },
  {
    value: 'shortForm',
    label: 'Template 3 - Short Form',
  },
];

export default function QuestionnaireReportTabs() {
  const [{ template }, { changeTemplate }] = useQuestionnaireReportSearchParams();
  const isFilterApplied = useIsQuestionnaireReportFilterApplied();

  const handleClick = (value: Template) => {
    changeTemplate(value);
  };

  return (
    <TabContext value={template}>
      <TabList
        sx={(theme) => ({
          gap: theme.spacing(0),
          '& .MuiTabs-flexContainer': {
            gap: theme.spacing(4),
          },
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        {TAB_LIST.map((tab) => (
          <Tab
            sx={(theme) => ({
              borderRadius: theme.shape.rounded(0),
              p: theme.spacing(1, 1.5),
              minWidth: 'unset',
              ':hover': { backgroundColor: 'transparent' },
            })}
            key={tab.value}
            label={tab.label}
            value={tab.value}
            onClick={() => handleClick(tab.value)}
          />
        ))}
      </TabList>
      {TAB_LIST.map((tab) => {
        const Content = TEMPLATE_CONTENT[tab.value];
        return (
          <TabPanel key={tab.value} value={tab.value}>
            {isFilterApplied ? (
              <Content />
            ) : (
              <Box
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.shape.rounded(2),
                  overflow: 'hidden',
                })}
              >
                <EmptyReportOverlay />
              </Box>
            )}
          </TabPanel>
        );
      })}
    </TabContext>
  );
}
